import React from "react";

// Source
import StorePreference from "../../store/Preference";
import StoreSetting from "../../store/Setting";
import StoreRoute from "../../store/Route";
import ControllerAlert from "../Alert";
import * as HelperSrc from "../../HelperSrc";
import * as ApiDocument from "../../api/custom/Document";
import * as ModelDocument from "../../model/custom/Document";
import * as ModelRoute from "../../model/Route";
import * as ModelCustomRoute from "../../model/custom/Route";
import * as ModelPreference from "../../model/Preference";
import * as ModelCustomPreference from "../../model/custom/Preference";
import * as ModelSetting from "../../model/Setting";
import * as ModelCustomSetting from "../../model/custom/Setting";

export default class ControllerDocumentCreate {
    // Variable
    private storePreference: ModelPreference.Istore<ModelCustomPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelCustomPreference.Idata | undefined>(undefined);
    private storeSetting: ModelSetting.Istore<ModelCustomSetting.Idata>;
    private storageSettingData = React.useRef<ModelCustomSetting.Idata | undefined>(undefined);
    private storeRoute: ModelRoute.Istore<ModelCustomRoute.Idata>;
    private storageRouteData = React.useRef<ModelCustomRoute.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementBottom = React.useRef<HTMLElement | null>(null);
    private elementClientName = React.useRef<HTMLInputElement | null>(null);
    private elementClientIndustry = React.useRef<HTMLInputElement | null>(null);
    private elementProjectName = React.useRef<HTMLInputElement | null>(null);
    private elementReportingYear = React.useRef<HTMLInputElement | null>(null);
    private elementTemplateTypeList = React.useRef<HTMLInputElement[] | null>(null);
    private elementEthicaDivider = React.useRef<HTMLInputElement | null>(null);
    private elementFileTemplateTnmmApa = React.useRef<HTMLInputElement | null>(null);
    private elementFileTemplateEa = React.useRef<HTMLInputElement | null>(null);
    private elementFileTemplateCs = React.useRef<HTMLInputElement | null>(null);
    private elementFileTemplateCompany1 = React.useRef<HTMLInputElement | null>(null);
    private elementFileTemplateCompany2 = React.useRef<HTMLInputElement | null>(null);
    private elementViewMain = React.useRef<HTMLElement | null>(null);

    private isRendered = React.useRef<boolean>(false);
    private validationForm = React.useRef<ModelDocument.Idata | undefined>(undefined);
    private language = React.useRef<string>("");

    getStoragePreference: ModelCustomPreference.Idata | undefined;
    getStorageSetting: ModelCustomSetting.Idata | undefined;
    getStorageRoute: ModelCustomRoute.Idata | undefined;

    getApiLoadingList: Record<string, boolean>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    getSelectOption: ModelDocument.IselectOption | undefined;
    setSelectOption: React.Dispatch<React.SetStateAction<ModelDocument.IselectOption | undefined>>;

    getValidationForm: ModelDocument.Idata | undefined;
    setValidationForm: React.Dispatch<React.SetStateAction<ModelDocument.Idata | undefined>>;

    getLanguage: string;
    setLanguage: React.Dispatch<React.SetStateAction<string>>;

    getElementClientIndustry: HTMLInputElement | null;
    setElementClientIndustry: React.Dispatch<React.SetStateAction<HTMLInputElement | null>>;

    getElementReportingYear: HTMLInputElement | null;
    setElementReportingYear: React.Dispatch<React.SetStateAction<HTMLInputElement | null>>;

    getFilenameList: Record<string, string>;
    setFilenameList: React.Dispatch<React.SetStateAction<Record<string, string>>>;

    getElementClientIndustryValue: string;
    setElementClientIndustryValue: React.Dispatch<React.SetStateAction<string>>;

    // Method
    constructor(isRendered: boolean) {
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();
        this.storeSetting = new StoreSetting();
        this.storageSettingData.current = this.storeSetting.storageGet();
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getStoragePreference] = React.useState<ModelCustomPreference.Idata | undefined>(this.storagePreferenceData.current);
        this.getStoragePreference = getStoragePreference;
        const [getStorageSetting] = React.useState<ModelCustomSetting.Idata | undefined>(this.storageSettingData.current);
        this.getStorageSetting = getStorageSetting;
        const [getStorageRoute] = React.useState<ModelCustomRoute.Idata | undefined>(this.storageRouteData.current);
        this.getStorageRoute = getStorageRoute;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getSelectOption, setSelectOption] = React.useState<ModelDocument.IselectOption | undefined>(undefined);
        this.getSelectOption = getSelectOption;
        this.setSelectOption = setSelectOption;

        const [getValidationForm, setValidationForm] = React.useState<ModelDocument.Idata | undefined>(undefined);
        this.getValidationForm = getValidationForm;
        this.setValidationForm = setValidationForm;

        const [getLanguage, setLanguage] = React.useState<string>(this.language.current);
        this.getLanguage = getLanguage;
        this.setLanguage = setLanguage;

        const [getElementClientIndustry, setElementClientIndustry] = React.useState<HTMLInputElement | null>(null);
        this.getElementClientIndustry = getElementClientIndustry;
        this.setElementClientIndustry = setElementClientIndustry;

        const [getElementReportingYear, setElementReportingYear] = React.useState<HTMLInputElement | null>(null);
        this.getElementReportingYear = getElementReportingYear;
        this.setElementReportingYear = setElementReportingYear;

        const [getFilenameList, setFilenameList] = React.useState<Record<string, string>>({});
        this.getFilenameList = getFilenameList;
        this.setFilenameList = setFilenameList;

        const [getElementClientIndustryValue, setElementClientIndustryValue] = React.useState<string>("");
        this.getElementClientIndustryValue = getElementClientIndustryValue;
        this.setElementClientIndustryValue = setElementClientIndustryValue;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_document_create", () => {
                this.initializeHtmlElement();
            });

            this.apiSelectOption();

            this.apiValidationForm();

            HelperSrc.storageEventListener("localization", () => {
                this.apiValidationForm();
            });
        }, []);

        React.useEffect(() => {
            this.storageSettingData.current = this.storeSetting.storageGet();
            this.storageRouteData.current = this.storeRoute.storageGet();
        }, [this.storageSettingData.current, this.storageRouteData.current]);

        React.useEffect(() => {
            this.storagePreferenceData.current = this.storePreference.storageGet();

            if (this.storagePreferenceData.current) {
                const initialLanguage = this.language.current;

                if (this.language.current !== this.storagePreferenceData.current.language) {
                    this.language.current = this.storagePreferenceData.current.language;
                    this.setLanguage(this.language.current);

                    if (initialLanguage !== "") {
                        this.apiSelectOption();
                    }
                }
            }
        }, [this.storagePreferenceData.current]);
    };

    clickDownloadStaticTemplate = (filename: string): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            downloadStaticTemplate: true
        }));

        ApiDocument.downloadStaticTemplate(filename, (response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                downloadStaticTemplate: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            const resultDownload = response.data.result as unknown as ModelDocument.Idownload;

            if (Object.entries(resultDownload).length > 0) {
                HelperSrc.downloadFromBrowser(resultDownload.file, resultDownload.filename);

                this.controllerAlert.show("success", response.message.success);
            }
        });
    };

    clickUpload = (tag: string): void => {
        const element = document.querySelector<HTMLInputElement>(tag);

        if (element) {
            element.click();
        }
    };

    returnFilename = (label: string, filelist: FileList | null): void => {
        if (label == "template_file") {
            this.setFilenameList((prev) => ({
                ...prev,
                template_file: filelist && filelist[0] ? filelist[0].name : ""
            }));
        } else if (label == "ea_template_file") {
            this.setFilenameList((prev) => ({
                ...prev,
                ea_template_file: filelist && filelist[0] ? filelist[0].name : ""
            }));
        } else if (label == "cs_image") {
            this.setFilenameList((prev) => ({
                ...prev,
                cs_image: filelist && filelist[0] ? filelist[0].name : ""
            }));
        } else if (label == "company_1_image") {
            this.setFilenameList((prev) => ({
                ...prev,
                company_1_image: filelist && filelist[0] ? filelist[0].name : ""
            }));
        } else if (label == "company_2_image") {
            this.setFilenameList((prev) => ({
                ...prev,
                company_2_image: filelist && filelist[0] ? filelist[0].name : ""
            }));
        }
    };

    clickBack = (): void => {
        if (this.storageRouteData.current) {
            HelperSrc.routerHistoryPush(this.storageRouteData.current.documentList.path);
        }
    };

    clickSave = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        const element = event.target as HTMLElement;

        const checkForm = HelperSrc.checkForm(element, this.validationForm.current);

        if (!checkForm) {
            return;
        }

        const clientName = this.elementClientName.current ? this.elementClientName.current.value : "";
        const clientIndustry = this.elementClientIndustry.current ? this.elementClientIndustry.current.value : "";
        const projectName = this.elementProjectName.current ? this.elementProjectName.current.value : "";
        const reportingYear = this.elementReportingYear.current ? this.elementReportingYear.current.value : "";
        const templateType = this.templateTypeSplit().length === 3 ? this.templateTypeSplit()[0] : "";
        const templateLanguage = this.templateTypeSplit().length === 3 ? `${this.templateTypeSplit()[1]}_${this.templateTypeSplit()[2]}` : "";
        const ethicalDivider = this.elementEthicaDivider.current ? this.elementEthicaDivider.current.checked.toString() : "";
        const templateFile =
            this.elementFileTemplateTnmmApa.current && this.elementFileTemplateTnmmApa.current.files
                ? this.elementFileTemplateTnmmApa.current.files[0]
                : "";
        const eaTemplateFile =
            this.elementFileTemplateEa.current && this.elementFileTemplateEa.current.files ? this.elementFileTemplateEa.current.files[0] : "";
        const csImage =
            this.elementFileTemplateCs.current && this.elementFileTemplateCs.current.files ? this.elementFileTemplateCs.current.files[0] : "";
        const company1Image =
            this.elementFileTemplateCompany1.current && this.elementFileTemplateCompany1.current.files
                ? this.elementFileTemplateCompany1.current.files[0]
                : "";
        const company2Image =
            this.elementFileTemplateCompany2.current && this.elementFileTemplateCompany2.current.files
                ? this.elementFileTemplateCompany2.current.files[0]
                : "";

        const formData = new FormData();
        formData.append("client_name", clientName);
        formData.append("client_industry", clientIndustry);
        formData.append("project_name", projectName);
        formData.append("reporting_year", reportingYear);
        formData.append("template_type", templateType);
        formData.append("template_language", templateLanguage);
        formData.append("ethical_divider", ethicalDivider);
        formData.append("template_file", templateFile);
        formData.append("ea_template_file", eaTemplateFile);
        formData.append("cs_image", csImage);
        formData.append("company_1_image", company1Image);
        formData.append("company_2_image", company2Image);

        this.setApiLoadingList((prev) => ({
            ...prev,
            create: true
        }));

        ApiDocument.createData(formData, (response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                this.setApiLoadingList((prev) => ({
                    ...prev,
                    create: false
                }));

                return;
            }

            const result = response.data.result as unknown as ModelDocument.Istatus;

            if (Object.entries(result).length > 0) {
                this.apiCheckTaskStatus(result);
            }
        });
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementBottom.current = document.querySelector<HTMLElement>(".bottom");
            this.elementClientName.current = document.querySelector<HTMLInputElement>("[name='client_name']");
            this.elementClientIndustry.current = document.querySelector<HTMLInputElement>("[name='client_industry']");
            this.elementProjectName.current = document.querySelector<HTMLInputElement>("[name='project_name']");
            this.elementReportingYear.current = document.querySelector<HTMLInputElement>("[name='reporting_year']");
            this.elementTemplateTypeList.current = Array.from(document.querySelectorAll<HTMLInputElement>("[name='template_type']"));
            this.elementEthicaDivider.current = document.querySelector<HTMLInputElement>("[name='ethical_divider']");
            this.elementFileTemplateTnmmApa.current = document.querySelector<HTMLInputElement>("[name='template_file']");
            this.elementFileTemplateEa.current = document.querySelector<HTMLInputElement>("[name='ea_template_file']");
            this.elementFileTemplateCs.current = document.querySelector<HTMLInputElement>("[name='cs_image']");
            this.elementFileTemplateCompany1.current = document.querySelector<HTMLInputElement>("[name='company_1_image']");
            this.elementFileTemplateCompany2.current = document.querySelector<HTMLInputElement>("[name='company_2_image']");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_document_create");

            this.setElementClientIndustry(this.elementClientIndustry.current);
            this.setElementReportingYear(this.elementReportingYear.current);
        }
    };

    private apiSelectOption = (): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            selectOption: true
        }));

        ApiDocument.selectOption((response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                selectOption: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.setSelectOption(response.data.result as unknown as ModelDocument.IselectOption);
        });
    };

    private apiValidationForm = (): void => {
        ApiDocument.validationForm((response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.validationForm.current = response.data.result as unknown as ModelDocument.Idata;
            this.setValidationForm(this.validationForm.current);

            if (this.elementBottom.current) {
                HelperSrc.validationFormHelper(this.elementBottom.current, this.validationForm.current);
            }
        });
    };

    private apiCheckTaskStatus = (result: ModelDocument.Istatus): void => {
        const timeout = setTimeout(() => {
            clearTimeout(timeout);

            ApiDocument.checkTaskStatus(result.task_name, (responseStatus) => {
                if (responseStatus instanceof Error || responseStatus.message.error) {
                    if (responseStatus instanceof Error) {
                        HelperSrc.writeLog("error", responseStatus.message);
                    } else {
                        this.controllerAlert.show("error", responseStatus.message.error, false);
                    }

                    this.setApiLoadingList((prev) => ({
                        ...prev,
                        create: false
                    }));

                    return;
                }

                const resultStatus = responseStatus.data.result as unknown as ModelDocument.Istatus;

                if (Object.entries(resultStatus).length > 0) {
                    ApiDocument.downloadDocx(resultStatus.download_path, (responseDownload) => {
                        this.setApiLoadingList((prev) => ({
                            ...prev,
                            create: false
                        }));

                        if (responseDownload instanceof Error || responseDownload.message.error) {
                            if (responseDownload instanceof Error) {
                                HelperSrc.writeLog("error", responseDownload.message);
                            } else {
                                this.controllerAlert.show("error", responseDownload.message.error, false);
                            }

                            return;
                        }

                        const resultDownload = responseDownload.data.result as unknown as ModelDocument.Idownload;

                        if (Object.entries(resultDownload).length > 0) {
                            HelperSrc.downloadFromBrowser(resultDownload.file, resultDownload.filename);

                            this.controllerAlert.show("success", responseDownload.message.success);

                            const storagePreferenceData = {
                                ...this.storagePreferenceData.current,
                                isShowPreviewDraftDialog: true,
                                isAutoSaveDraft: true
                            } as ModelCustomPreference.Idata;

                            this.storePreference.storageSet(storagePreferenceData);
                        }
                    });
                } else {
                    this.apiCheckTaskStatus(result);
                }
            });
        }, HelperSrc.STATUS_TIMEOUT);
    };

    private templateTypeSplit = (): string[] => {
        let result: string[] = [];

        if (this.elementTemplateTypeList.current) {
            for (const element of this.elementTemplateTypeList.current) {
                if (element.checked) {
                    result = element.value.split("_");

                    break;
                }
            }
        }

        return result;
    };
}
